import React, { useEffect, useState } from 'react';
import { useSetRecoilState } from 'recoil';
import { SelectValue } from 'antd/lib/select';

import './index.less';

import {
  OCRProviderModel,
  OCRProviderReferenceSuiteOutput,
  OCRProviderReferencePolicy,
  OCRProviderImagesMode,
  OCRProviderReferenceSuiteStatus,
  OCRProviderReferencePolicyStatus,
  OCRProviderReferenceSuiteOverlay,
} from '../../../gamma-api';
import { getTranslateFunc } from '../../utils/stringUtil';
import IbSwitch from '../common/IbSwitch';
import { defaultApi } from '../../apis';
import { LARGE_PAGE_SIZE } from '../../constants';
import { alertsSelectorAdd } from '../../recoil';
import IbSelect from '../common/IbSelect';
import IbTypography from '../common/IbTypography';
import Switcher from '../common/Switcher';

import { MAIN_CLASS_NAME, GROUP_CLASS_NAME, BLOCK_CLASS_NAME, TITLE_CLASS_NAME } from './const';

export interface IOcrProps {
  suite?: OCRProviderReferenceSuiteOutput | null;
  overlays?: OCRProviderReferenceSuiteOverlay[];
  onChange?: (suite: OCRProviderReferenceSuiteOutput) => void;
}

const Ocr: React.FC<IOcrProps> = ({ suite, overlays, onChange }) => {
  const translate = getTranslateFunc('Recognizer selector: OCR:');

  const addAlert = useSetRecoilState(alertsSelectorAdd);

  const [providers, setProviders] = useState<OCRProviderModel[]>([]);

  const [loading, setLoading] = useState(true);

  const editingDisabled = suite?.overlay !== OCRProviderReferenceSuiteOverlay.Overrides;
  const suiteDisabled = suite?.status !== OCRProviderReferencePolicyStatus.Enabled;

  const loadProviders = async () => {
    setLoading(true);

    try {
      const response = await defaultApi.searchOcrProviders(0, LARGE_PAGE_SIZE, false);
      setProviders(response.data.items || []);
    } catch (err) {
      addAlert({
        content: translate('Failed to load'),
        type: 'error',
      });
      // eslint-disable-next-line no-console
      console.error(err);
    }

    setLoading(false);
  };

  useEffect(() => {
    loadProviders().finally();
  }, []);

  const onStatusToggle = (value: string | boolean) => {
    if (!suite) {
      return;
    }

    onChange?.({
      ...suite,
      status: value ? OCRProviderReferenceSuiteStatus.Enabled : OCRProviderReferenceSuiteStatus.Disabled,
    });
  };

  const onOverlayToggle = (value: string | boolean) => {
    if (!suite) {
      return;
    }

    onChange?.({
      ...suite,
      overlay: value as OCRProviderReferenceSuiteOverlay,
    });
  };

  const onPolicyToggle = (index: number) => (value: boolean) => {
    if (!suite) {
      return;
    }

    onChange?.({
      ...suite,
      policies: suite.policies.map((policy, idx) =>
        idx === index
          ? {
              ...policy,
              status: value ? OCRProviderReferencePolicyStatus.Enabled : OCRProviderReferencePolicyStatus.Disabled,
            }
          : policy
      ),
    });
  };

  const onProviderChange = (index: number) => (value: SelectValue) => {
    if (!suite) {
      return;
    }

    onChange?.({
      ...suite,
      policies: suite.policies.map((policy, idx) =>
        idx === index ? { ...policy, provider_id: value as string } : policy
      ),
    });
  };

  const onImagesChange = (value: boolean) => {
    if (!suite) {
      return;
    }

    onChange?.({
      ...suite,
      images_mode: value ? OCRProviderImagesMode.Recognized : OCRProviderImagesMode.Optimized,
    });
  };

  const overlaysOptions = (overlays || []).map((overlay) => ({
    value: overlay,
    label: translate(`Overlay: ${overlay}`),
  }));

  const providerOptions = providers.map((provider) => ({
    value: provider.ocr_provider_id,
    label: `${provider.settings.type}: ${provider.name}`,
  }));

  const renderStatus = () => {
    return (
      <div className={GROUP_CLASS_NAME}>
        <div className={BLOCK_CLASS_NAME}>
          <div className={TITLE_CLASS_NAME}>
            <IbTypography>
              <IbSwitch
                checked={suite?.status === OCRProviderReferencePolicyStatus.Enabled}
                size="small"
                onChange={onStatusToggle}
              />
              <IbTypography.Paragraph strong type={overlays !== undefined ? 'secondary' : 'normal'}>
                {translate('Status: Title')}
              </IbTypography.Paragraph>
            </IbTypography>
            <IbTypography.Paragraph type="secondary">{translate('Status: Description')}</IbTypography.Paragraph>
          </div>
        </div>
      </div>
    );
  };

  const renderOverlay = () => {
    if (!overlaysOptions.length) {
      return null;
    }

    if (suiteDisabled) {
      return null;
    }

    return (
      <div className={GROUP_CLASS_NAME}>
        <div className={BLOCK_CLASS_NAME}>
          <IbTypography.Paragraph strong className={TITLE_CLASS_NAME} type="secondary">
            {translate('Overlay: Title')}
          </IbTypography.Paragraph>
          <Switcher options={overlaysOptions} value={suite?.overlay} onChange={onOverlayToggle} />
        </div>
      </div>
    );
  };

  const renderTitle = (policy: OCRProviderReferencePolicy, index: number) => {
    return (
      <div className={BLOCK_CLASS_NAME}>
        <div className={TITLE_CLASS_NAME}>
          <IbTypography>
            <IbSwitch
              checked={policy.status == OCRProviderReferencePolicyStatus.Enabled}
              disabled={editingDisabled}
              size="small"
              onChange={onPolicyToggle(index)}
            />
            <IbTypography.Paragraph strong disabled={editingDisabled} type="secondary">
              {translate(`Media: ${policy.media_groups[0]}`)}
            </IbTypography.Paragraph>
          </IbTypography>
        </div>
      </div>
    );
  };

  const renderProvider = (policy: OCRProviderReferencePolicy, index: number) => {
    if (policy.status !== OCRProviderReferencePolicyStatus.Enabled) {
      return null;
    }

    return (
      <div className={BLOCK_CLASS_NAME}>
        <IbTypography.Paragraph strong className={TITLE_CLASS_NAME} disabled={editingDisabled} type="secondary">
          {translate('Provider: Title')}
        </IbTypography.Paragraph>
        <IbSelect
          disabled={editingDisabled}
          loading={loading}
          options={providerOptions}
          value={policy.provider_id || undefined}
          onChange={onProviderChange(index)}
        />
      </div>
    );
  };

  const renderPolicy = (policy: OCRProviderReferencePolicy, index: number) => {
    return (
      <div key={index} className={GROUP_CLASS_NAME}>
        {renderTitle(policy, index)}
        {renderProvider(policy, index)}
      </div>
    );
  };

  const renderPolicies = () => {
    if (suiteDisabled) {
      return null;
    }

    return suite?.policies.map(renderPolicy);
  };

  const renderExtra = () => {
    if (suiteDisabled) {
      return null;
    }

    if (!suite?.policies.filter((policy) => policy.status === OCRProviderReferencePolicyStatus.Enabled).length) {
      return null;
    }

    return (
      <div className={GROUP_CLASS_NAME}>
        <div className={BLOCK_CLASS_NAME}>
          <div className={TITLE_CLASS_NAME}>
            <IbTypography>
              <IbSwitch
                checked={suite.images_mode === OCRProviderImagesMode.Recognized}
                disabled={editingDisabled}
                size="small"
                onChange={onImagesChange}
              />
              <IbTypography.Paragraph strong disabled={editingDisabled} type="secondary">
                {translate('Images: Title')}
              </IbTypography.Paragraph>
            </IbTypography>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={MAIN_CLASS_NAME}>
      {renderStatus()}
      {renderOverlay()}
      {renderPolicies()}
      {renderExtra()}
    </div>
  );
};

export default Ocr;
